import { useAtom } from "jotai";
import { useMemo, useState, useEffect, useCallback } from "react";

import { handleAssetUrl } from "../../../shared/utils";
import { QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS } from "../../widget.constants";
import {
  currentPageAtom,
  currentQuestionIndexAtom,
  quizAtom,
  smsAtom,
} from "../../widget.state";
import { QuizVariantEnum } from "../../widget.types";

export function QuizHeader() {
  const [quiz] = useAtom(quizAtom);
  const [sms] = useAtom(smsAtom);
  const [currentQuestionIndex] = useAtom(currentQuestionIndexAtom);
  const [currentPage] = useAtom(currentPageAtom);
  const [confettiQuestionIndex, setConfettiQuestionIndex] = useState<
    number | null
  >(null);
  const [
    lastCustomConfettiQuestionIndex,
    setLastCustomConfettiQuestionIndex,
  ] = useState<number>(-1);

  const questionsPerPage = window.FKL.questionsPerPage;
  const totalQuestions = quiz.questions.length;
  const completedQuestionsDotsCounter =
    questionsPerPage === 1 ? currentPage : currentQuestionIndex;

  const totalEntries = useMemo(
    () =>
      quiz.questions.reduce(
        (acc, question) =>
          acc +
          (question.options.some((q) => q.isSelected && q.isCorrect) ? 1 : 0),
        (quiz.allTimePoints ?? 0) +
          ((quiz.prevSmsExtraEntries ?? 0) + sms.extraEntries)
      ),
    [
      quiz.allTimePoints,
      quiz.questions,
      sms.extraEntries,
      quiz.prevSmsExtraEntries,
    ]
  );

  const isConfettiCustomControl = useCallback(
    (questionIndex: number) => {
      if (questionIndex > 0 && questionsPerPage > 1) {
        const lastQuestionIndex = questionIndex - 1;
        const lastQuestion = quiz.questions[lastQuestionIndex];
        const isSingleQuestionLastQuestion = !(
          (lastQuestion.smsInfo?.brandName &&
            lastQuestion.smsInfo.question &&
            lastQuestion.smsInfo.smsBrandPrivacyPolicy &&
            lastQuestion.smsInfo.tacSmsBrand) ||
          lastQuestion.cpl?.title
        );
        const isLastQuestionAnswered = quiz.isArcade
          ? lastQuestion.options.some((option) => option.isSelected)
          : lastQuestion.options.some(
              (option) => option.isCorrect && option.isSelected
            );
        const isReminderOnLastQuestion: boolean =
          quiz.variant.toUpperCase() === QuizVariantEnum.Quiz &&
          window.FKL.allowOptInReminder &&
          (quiz.submissionCount || 1) > 1 &&
          currentPage === 0 &&
          lastQuestionIndex === 0;

        return (
          isSingleQuestionLastQuestion &&
          isLastQuestionAnswered &&
          !isReminderOnLastQuestion
        );
      }
      return false;
    },
    [
      currentPage,
      questionsPerPage,
      quiz.isArcade,
      quiz.questions,
      quiz.submissionCount,
      quiz.variant,
    ]
  );

  useEffect(() => {
    const activeQuestionIndex =
      questionsPerPage === 1 ? currentPage : currentQuestionIndex;
    if (
      isConfettiCustomControl(activeQuestionIndex) &&
      lastCustomConfettiQuestionIndex < activeQuestionIndex - 1
    ) {
      setConfettiQuestionIndex(quiz.questions.length);
      setTimeout(() => {
        setConfettiQuestionIndex(null);
      }, QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS * 1000);
      setLastCustomConfettiQuestionIndex(activeQuestionIndex - 1);
    } else {
      if (
        confettiQuestionIndex === null &&
        quiz.questions[activeQuestionIndex]?.options.some(
          (q) => q.isSelected && q.isCorrect
        )
      ) {
        setConfettiQuestionIndex(activeQuestionIndex);
      } else if (
        confettiQuestionIndex !== null &&
        confettiQuestionIndex < activeQuestionIndex
      ) {
        setConfettiQuestionIndex(null);
      }
    }
  }, [
    confettiQuestionIndex,
    currentPage,
    currentQuestionIndex,
    questionsPerPage,
    quiz.questions,
    isConfettiCustomControl,
    lastCustomConfettiQuestionIndex,
  ]);

  return (
    <div className="quiz--single-page__header--v2">
      <div className="quiz--single-page__header__title">
        ANSWER ALL QUESTIONS TO CLAIM YOUR EXTRA ENTRIES IN THE{" "}
        <strong>
          {window.FKL.sweepstakePrice} FOR THE {window.FKL.sweepstakeTitle}!
        </strong>
      </div>
      <div
        className="quiz--single-page__header__wrapper"
        style={{
          backgroundImage:
            confettiQuestionIndex !== null
              ? `url(${handleAssetUrl("confetti.gif")})`
              : "none",
        }}
      >
        <div className="quiz--single-page__header__total-entries__container">
          YOUR ENTRIES
          <div className="quiz--single-page__header__total-entries">
            {totalEntries}
          </div>
        </div>
        <div className="quiz--single-page__header__progress">
          {[...Array(totalQuestions)].map((_, i) => (
            <div
              key={`progress-dot-${i}`}
              className={`progress-dot ${
                i < completedQuestionsDotsCounter && "completed"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
